import { useEffect, useState } from 'react';
import { Spinner, Stack } from 'react-bootstrap';
import { FaArrowLeft } from 'react-icons/fa6';
import { useNavigate, useParams } from 'react-router-dom';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { DetailSection, LabelText } from '../../../../../components/generic';
import ConfirmationModal from '../../../../../components/modals/ConfirmationModal';
import YesNoCell from '../../../../../components/tables/cellComponents/YesNoCell';
import { useToast } from '../../../../../context/ToastContext';
import {
  useDeleteTeamProfileMutation,
  useGetTeamProfileQuery
} from '../../../../../services/endpoints/website/teamProfile';
import { TeamProfile } from '../../../../../types/website.types';
import { dateToUTC } from '../../../../../utils/dateUtils';
import { getErrorMessage } from '../../../../../utils/utils';

export const TeamProfileDetails = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { data, error, isLoading } = useGetTeamProfileQuery(+params.id!);
  const [teamProfile, setTeamProfile] = useState<TeamProfile>();
  const [deleteTeamProfile, { isLoading: isDeleting }] = useDeleteTeamProfileMutation();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (error && !isLoading) {
      addToast('Error while loading team profile', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (data) {
      setTeamProfile(data.teamProfile);
    }
  }, [data]);

  const handleDelete = async () => {
    try {
      await deleteTeamProfile(+params.id!).unwrap();
      addToast('Team profile deleted successfully', 'success');
      navigate('/admin/website/team-profiles');
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  if (isLoading || !teamProfile) {
    return (
      <div className="w-100 vh-100 d-inline-flex">
        <Spinner
          variant="primary"
          className="m-auto"
        />
      </div>
    );
  }

  return (
    <>
      <div
        className="d-flex justify-content-between align-items-center px-lg-5 border-bottom px-4 py-3"
        style={{ background: '#EFEFEF' }}
      >
        <Stack
          direction="horizontal"
          gap={2}
          className="ps-lg-3 ps-0"
        >
          <button
            className="btn btn-link text-info p-0"
            onClick={() => navigate('/admin/website/team-profiles')}
          >
            <FaArrowLeft
              size={20}
              className="d-lg-none"
            />
            <FaArrowLeft
              size={24}
              className="d-none d-lg-block"
            />
          </button>
          <h2
            className="d-lg-none text-secondary fw-semibold text-uppercase mb-0 pt-1"
            style={{ color: '#888888', fontSize: '1rem' }}
          >
            Team Profiles List
          </h2>
          <h2 className="d-none d-lg-block text-info fw-bold mb-0">{`Team Profile #${teamProfile?.id}`}</h2>
        </Stack>
        <Stack
          direction="horizontal"
          gap={1}
          className="d-none d-lg-flex"
        >
          <SimpleButton
            variant="primary"
            height={36}
            onClick={() => navigate(`/admin/website/team-profiles/${teamProfile?.id}/edit`)}
          >
            Edit Team Profile
          </SimpleButton>
          <SimpleButton
            variant="primary"
            height={36}
            onClick={() => setShowModal(true)}
          >
            Delete Team Profile
          </SimpleButton>
        </Stack>
      </div>
      <div>
        <Stack
          gap={4}
          className="p-4"
        >
          <DetailSection>
            <LabelText
              label="Name:"
              text={teamProfile?.name}
            />
            <LabelText
              label="Description:"
              text={teamProfile?.description}
            />
            <LabelText
              label="Quote:"
              text={teamProfile?.quote}
            />
            <LabelText
              label="Quote Type:"
              text={teamProfile?.quote_type}
            />
            <LabelText
              label="Title:"
              text={teamProfile?.title}
            />
            <LabelText label="Is HQ:">
              <YesNoCell value={teamProfile?.is_hq} />
            </LabelText>
            <LabelText
              label="Position:"
              text={teamProfile?.position}
            />
            <LabelText
              label="Image File Name:"
              text={teamProfile?.image_file_name}
            />
            <LabelText
              label="Image Content Type:"
              text={teamProfile?.image_content_type}
            />
            <LabelText
              label="Image File Size:"
              text={teamProfile?.image_file_size}
            />
            <LabelText
              label="Image Updated At:"
              text={
                teamProfile?.image_updated_at
                  ? dateToUTC(new Date(teamProfile?.image_updated_at), 'MM/dd/yyyy')
                  : 'N/A'
              }
            />
            <LabelText
              label="Created At:"
              text={
                teamProfile?.created_at
                  ? dateToUTC(new Date(teamProfile?.created_at), 'MM/dd/yyyy')
                  : 'N/A'
              }
            />
            <LabelText
              label="Updated At:"
              text={
                teamProfile?.updated_at
                  ? dateToUTC(new Date(teamProfile?.updated_at), 'MM/dd/yyyy')
                  : 'N/A'
              }
            />
          </DetailSection>
        </Stack>
      </div>
      <ConfirmationModal
        show={showModal}
        title="Delete Team Profile"
        message="Are you sure you want to delete this team profile?"
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowModal(false)}
        isLoading={isDeleting}
      />
    </>
  );
};
